import type { IProxy } from '@hit-game/shared/types';
import { useEffect, useState } from 'react';

import { maskProxy } from '../../utils/format';
import { Button } from '../common/Button';
import { Modal } from '../common/Modal';
import { FIELD_CLASS_MONO as FIELD_CLASS, LABEL_CLASS } from '../common/form.styles';

interface AddModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (values: string[]) => Promise<boolean>;
}

export function ProxyAddModal({ open, onClose, onSubmit }: AddModalProps) {
  const [text, setText] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSubmit = async () => {
    const values = text
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean);
    if (values.length === 0) return;

    setSaving(true);
    try {
      if (await onSubmit(values)) {
        setText('');
        onClose();
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      busy={saving}
      title="Thêm Proxy"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={saving}>
            Hủy
          </Button>
          <Button
            onClick={() => {
              void handleSubmit();
            }}
            loading={saving}
            disabled={saving || !text.trim()}
            className="px-5"
          >
            {saving ? 'Đang thêm...' : 'Thêm proxy'}
          </Button>
        </>
      }
    >
      <label htmlFor="proxy-bulk" className={LABEL_CLASS}>
        Nhập proxy (mỗi dòng một proxy)
      </label>
      <textarea
        id="proxy-bulk"
        value={text}
        onChange={(e) => {
          setText(e.target.value);
        }}
        placeholder={'host:port:user:pass\nhost:port\n103.69.96.15:7777:user:pass'}
        rows={8}
        className={`${FIELD_CLASS} resize-none`}
      />
      <p className="text-ink-faint mt-2 text-[11px]">
        Hỗ trợ định dạng:{' '}
        <code className="bg-panel-hover text-accent-bright rounded px-1.5 py-0.5 font-mono">
          host:port:user:pass
        </code>{' '}
        hoặc{' '}
        <code className="bg-panel-hover text-accent-bright rounded px-1.5 py-0.5 font-mono">
          host:port
        </code>
      </p>
    </Modal>
  );
}

interface EditModalProps {
  proxy: IProxy | null;
  onClose: () => void;
  onSave: (id: string, value: string) => Promise<boolean>;
}

export function ProxyEditModal({ proxy, onClose, onSave }: EditModalProps) {
  const [value, setValue] = useState('');
  const [saving, setSaving] = useState(false);

  // Đồng bộ khi mở sang proxy khác — không thì ô nhập giữ giá trị của dòng trước.
  useEffect(() => {
    setValue(proxy?.value ?? '');
  }, [proxy]);

  const handleSave = async () => {
    const trimmed = value.trim();
    if (!trimmed || !proxy?._id || saving) return;

    setSaving(true);
    try {
      if (await onSave(proxy._id, trimmed)) onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={proxy !== null}
      onClose={onClose}
      busy={saving}
      maxWidth={480}
      title={
        <>
          Sửa Proxy: <span className="text-accent">{proxy ? maskProxy(proxy.value) : ''}</span>
        </>
      }
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={saving}>
            Hủy
          </Button>
          <Button
            onClick={() => {
              void handleSave();
            }}
            loading={saving}
            disabled={saving || !value.trim()}
            className="px-5"
          >
            {saving ? 'Đang lưu...' : 'Lưu'}
          </Button>
        </>
      }
    >
      <label htmlFor="proxy-value" className={LABEL_CLASS}>
        Giá trị proxy
      </label>
      <input
        id="proxy-value"
        type="text"
        value={value}
        onChange={(e) => {
          setValue(e.target.value);
        }}
        placeholder="host:port:user:pass"
        className={FIELD_CLASS}
      />
    </Modal>
  );
}
